import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'

import type { FieldMeta } from '@/lib/fit-profile'
import type { FieldKind } from '@/lib/fit-data'

/**
 * Extension point for in-place field editing in the FIT data view.
 *
 * Every value cell in the Sessions / Laps / Devices / Records tables and
 * the All-messages field tables is rendered through `useFieldCell()`.
 * With no renderer provided the cell shows `defaultContent` (the
 * formatted value). The workspace supplies `renderEditableFieldCell`
 * (`render-editable-field-cell.ts`) via `<FitDataView renderFieldCell={...}>`:
 * it wraps that content with an "edit" affordance opening a dialog that
 * dispatches fit-core's `editField` operation, using the context below to
 * know exactly which message instance / field the cell belongs to.
 *
 * Contract for implementers:
 * - `source === 'original'` cells show the unedited snapshot; editing
 *   them makes no sense (edits always apply on top of the current state),
 *   so a renderer should return `defaultContent` unchanged for those.
 * - `messagesKey` is `undefined` for preserved unknown message types
 *   (they have no SDK messagesKey and live in `preservedUnknownMessages`).
 * - `messageIndex` indexes `messagesByKey(activity).get(messagesKey)` --
 *   i.e. `activity.records[i]`, `activity.laps[i]`,
 *   `activity.otherMessages[messagesKey][i]`, or 0 for the singleton
 *   `fileId` / `activitySummary`; for unknown message types it indexes
 *   the instances of that global message number in
 *   `preservedUnknownMessages` order.
 */
export interface FieldCellContext {
  source: 'current' | 'original'
  /**
   * Which table the cell is in: a per-type message table (sessions, laps,
   * devices), the windowed Records table, or the All-messages view.
   */
  surface: 'messageTable' | 'records' | 'allMessages'
  messagesKey: string | undefined
  /** Global message number, when known. */
  mesgNum: number | undefined
  messageIndex: number
  /** Decoded object key (`heartRate`, `"123"` for undocumented), or `dev:<key>` for a developer field. */
  fieldId: string
  kind: FieldKind
  value: unknown
  units: string
  meta: FieldMeta | undefined
}

export type FieldCellRenderer = (context: FieldCellContext, defaultContent: ReactNode) => ReactNode

export const FieldCellRendererContext = createContext<FieldCellRenderer | null>(null)

/** Returns a function rendering one value cell's content, through the provided renderer if any. */
export function useFieldCell(): (
  context: FieldCellContext,
  defaultContent: ReactNode,
) => ReactNode {
  const renderer = useContext(FieldCellRendererContext)
  return renderer ?? passthrough
}

function passthrough(_context: FieldCellContext, defaultContent: ReactNode): ReactNode {
  return defaultContent
}
